import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Radar, Receipt, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, DecisionChip, Empty, Kpi, Loading, PageHeader, Panel, SeverityChip, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError, decisionPayload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const BLANK = {
  donor_id: "", fund_id: "", amount: 25000, currency: "INR",
  donation_date: new Date().toISOString().slice(0, 10), giving_mode: "one_off",
  restriction: "unrestricted", designation: "", campaign: "Annual Appeal 2026",
  payment_method: "bank_transfer", fund_source: "domestic", receipt_required: true, purpose_code: "",
};

export default function Donations() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const detail = useRowDetail("donation", () => load());
  const [donors, setDonors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [preflight, setPreflight] = useState(null);

  const load = useCallback(async () => {
    const [d, dn, f] = await Promise.all([
      api.get("/charity/donations"), api.get("/charity/donors"), api.get("/charity/funds"),
    ]);
    setRows(d.data);
    setDonors(dn.data);
    setFunds(f.data);
  }, []);

  useEffect(() => { load().catch(() => setRows([])); }, [load]);

  const runPreflight = async () => {
    if (!form.donor_id || !form.fund_id) return toast.error("Select a donor and a fund first");
    setBusy("preflight");
    try {
      const { data } = await api.post("/charity/donations/preflight", form);
      setPreflight(data);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.post("/charity/donations", form);
      setPreflight(data.decision);
      toast.success(data.donation.status === "pending_approval"
        ? `${data.donation.reference} recorded — awaiting second-person approval`
        : `${data.donation.reference} posted · receipt ${data.donation.receipt_number}`);
      setOpen(false);
      setPreflight(null);
      await load();
    } catch (e) {
      const dec = decisionPayload(e);
      if (dec) {
        setPreflight(dec);
        toast.error("Blocked by the Regulatory Decision Engine");
      } else {
        toast.error(apiError(e));
      }
    }
    setBusy("");
  };

  if (!rows) return <Loading label="Loading donation ledger" />;
  const blocked = rows.filter((r) => r.decision === "BLOCK").length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-006 · Donation Entry"
        title="Every gift passes the engine before it hits the ledger"
        description="Restriction classification, receipting and a live regulatory pre-flight check. Blocked gifts never post; gifts requiring approval wait in the maker-checker queue."
        actions={can("donation", "write") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPreflight(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-donation-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> Record donation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-border bg-card">
              <DialogHeader>
                <DialogTitle>Record a donation</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Run the pre-flight check to see the decision before committing the gift.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <F label="Donor" full>
                  <Select value={form.donor_id} onValueChange={(v) => setForm({ ...form, donor_id: v })}>
                    <SelectTrigger data-testid="donation-donor-select" className="bg-muted/40">
                      <SelectValue placeholder="Select donor" />
                    </SelectTrigger>
                    <SelectContent>
                      {donors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name} · {d.country}{d.sanctions_hit ? " · SANCTIONS HIT" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Fund">
                  <Select value={form.fund_id} onValueChange={(v) => setForm({ ...form, fund_id: v })}>
                    <SelectTrigger data-testid="donation-fund-select" className="bg-muted/40">
                      <SelectValue placeholder="Select fund" />
                    </SelectTrigger>
                    <SelectContent>
                      {funds.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.code} · {f.fund_type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Restriction">
                  <Select value={form.restriction} onValueChange={(v) => setForm({ ...form, restriction: v })}>
                    <SelectTrigger data-testid="donation-restriction-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["unrestricted", "restricted", "designated"].map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Amount">
                  <Input type="number" value={form.amount}
                         onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                         data-testid="donation-amount-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Currency">
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="donation-currency-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["INR", "USD", "GBP", "EUR", "SGD", "KES"].map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Payment method">
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger data-testid="donation-payment-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["bank_transfer", "cash", "card", "upi", "cheque"].map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Giving mode">
                  <Select value={form.giving_mode} onValueChange={(v) => setForm({ ...form, giving_mode: v })}>
                    <SelectTrigger data-testid="donation-mode-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["one_off", "recurring", "micro", "crowdfunding", "tithing", "csr", "legacy", "grant"].map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Donation date">
                  <Input type="date" value={form.donation_date}
                         onChange={(e) => setForm({ ...form, donation_date: e.target.value })}
                         data-testid="donation-date-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Designation / purpose">
                  <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}
                         data-testid="donation-designation-input" className="bg-muted/40" />
                </F>
                <div className="flex items-center justify-between sm:col-span-2">
                  <span className="text-xs text-muted-foreground">Receipt required (80G / Form 10BD)</span>
                  <Switch checked={form.receipt_required}
                          onCheckedChange={(v) => setForm({ ...form, receipt_required: v })}
                          data-testid="donation-receipt-toggle" />
                </div>
              </div>

              {preflight && (
                <div className={`rounded border p-3 ${preflight.decision === "BLOCK"
                  ? "border-red-500/30 bg-red-500/10" : preflight.decision === "PASS"
                    ? "border-emerald-500/30 bg-emerald-500/10" : "border-amber-500/30 bg-amber-500/10"}`}
                     data-testid="donation-regulatory-check-banner">
                  <div className="flex items-center gap-2">
                    <Radar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-foreground/80">Pre-flight decision</span>
                    <DecisionChip decision={preflight.decision} testId="donation-preflight-decision" />
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {preflight.triggered.map((t) => (
                      <div key={t.rule_key} className="flex items-start gap-2 text-[11px]"
                           data-testid={`preflight-rule-${t.rule_key}`}>
                        <SeverityChip severity={t.effective_severity} />
                        <div>
                          <span className="font-mono text-primary">{t.rule_key}</span>
                          <span className="ml-2 text-muted-foreground">{t.title}</span>
                          {t.remediation && <div className="text-emerald-600 dark:text-emerald-400/80">{t.remediation}</div>}
                        </div>
                      </div>
                    ))}
                    {preflight.triggered.length === 0 && (
                      <div className="text-[11px] text-emerald-600 dark:text-emerald-400">No rule fired — clear to post.</div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={runPreflight} disabled={busy === "preflight"}
                        data-testid="donation-preflight-button" className="rounded-full border-border">
                  {busy === "preflight" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Radar className="mr-1.5 h-4 w-4" />}
                  Pre-flight check
                </Button>
                <Button onClick={save} disabled={busy === "save" || !form.donor_id || !form.fund_id}
                        data-testid="donation-save-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Post donation
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-donation-count" label="Gifts recorded" value={rows.length} accent="sky" />
        <Kpi testId="kpi-donation-value" label="Recognised value" accent="emerald" icon={Receipt}
             value={money(rows.reduce((a, r) => a + (r.base_amount || 0), 0), "INR", true)} />
        <Kpi testId="kpi-donation-pending" label="Awaiting approval" accent="amber"
             value={rows.filter((r) => r.status === "pending_approval").length} />
        <Kpi testId="kpi-donation-blocked" label="Blocked attempts" value={blocked} accent="red" icon={ShieldAlert} />
      </div>

      <Panel testId="panel-donations" kicker="Ledger-linked" title="Donations">
        <Table testId="donations-table"
               head={<><Th>Reference</Th><Th>Date</Th><Th>Donor</Th><Th>Fund</Th><Th>Restriction</Th><Th>Amount</Th><Th>Decision</Th><Th>Status</Th><Th>Receipt</Th></>}>
          {rows.length === 0 && <Empty colSpan={9} label="No donations recorded" />}
          {rows.map((r) => (
            <tr key={refOf(r)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(r, refOf(r)))} data-testid={`donation-row-${r.reference}`}>
              <Td className="font-mono text-[11px] text-primary">{r.reference}</Td>
              <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(r.donation_date)}</Td>
              <Td className="max-w-[180px] truncate">{r.donor_name}</Td>
              <Td className="font-mono text-[11px]">{r.fund_code}</Td>
              <Td>
                <Chip className={r.restriction === "unrestricted"
                  ? "border-border bg-muted text-foreground/80"
                  : "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300"}>{r.restriction}</Chip>
              </Td>
              <Td className="font-mono">
                {money(r.amount, r.currency)}
                {r.currency !== "INR" && (
                  <div className="text-[10px] text-muted-foreground/70">≈ {money(r.base_amount, "INR", true)}</div>
                )}
              </Td>
              <Td><DecisionChip decision={r.decision} testId={`donation-decision-${r.reference}`} /></Td>
              <Td>
                <Chip className={r.status === "posted"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>{r.status.replace(/_/g, " ")}</Chip>
              </Td>
              <Td className="font-mono text-[10px] text-muted-foreground">{r.receipt_number || "—"}</Td>
            </tr>
          ))}
        </Table>
      </Panel>

      {detail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
